import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializePaddle } from '@paddle/paddle-js';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './PaymentPage.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';
import { toast } from 'react-toastify';
import { getApiUrl } from '../api';

const PLAN_INFO = {
  starter: {
    name: 'Starter Plan',
    monthlyPrice: 5,
    annualPrice: 55,
    details: '1 location · Google reviews · 48hr response time',
  },
  pro: {
    name: 'Pro Plan',
    monthlyPrice: 10,
    annualPrice: 110,
    details: 'Multiple platforms · Priority response time · Advanced personalization',
  },
};

const PaymentPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state || {};
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [paddle, setPaddle] = useState(null);
  const [prices, setPrices] = useState(null);
  const [configError, setConfigError] = useState(false);
  const selectedPlan = PLAN_INFO[incoming.plan] ? incoming.plan : 'starter';
  const planDetails = PLAN_INFO[selectedPlan];

  useEffect(() => {
    const loadPaddle = async () => {
      const res = await fetch(getApiUrl('/api/payment/config'));
      if (!res.ok) {
        setConfigError(true);
        return;
      }
      const config = await res.json();
      if (!config.clientToken || !config.prices) {
        setConfigError(true);
        return;
      }
      setPrices(config.prices);
      const paddleInstance = await initializePaddle({
        environment: config.environment,
        token: config.clientToken,
      });
      setPaddle(paddleInstance || null);
      if (!paddleInstance) setConfigError(true);
    };
    loadPaddle();
  }, []);

  const validateEmail = () => {
    if (!email) {
      setEmailError('Email address is required.');
      return false;
    }
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const startCheckout = () => {
    if (!validateEmail()) {
      toast.error('Please enter a valid email for the receipt.');
      return;
    }
    if (!paddle || !prices) {
      toast.error(configError ? 'Checkout is not available right now. Please try again shortly.' : 'Checkout is still loading, please wait a moment.');
      return;
    }
    const priceId = prices[selectedPlan]?.[billingCycle];
    if (!priceId) {
      toast.error('Selected plan is not available');
      return;
    }

    const storedUser = JSON.parse(localStorage.getItem('user1') || 'null');

    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: { email },
      customData: {
        auditRequestId: incoming.auditRequestId || null,
        userId: storedUser?.id || null,
        planKey: `${selectedPlan}_${billingCycle}`,
        customerEmail: email,
      },
      settings: {
        successUrl: incoming.auditRequestId
          ? `${window.location.origin}/audit/${incoming.auditRequestId}`
          : `${window.location.origin}/dashboard`,
      },
    });
  };

  const activePrice = billingCycle === 'monthly' ? planDetails.monthlyPrice : Math.round(planDetails.annualPrice / 12);

  return (
    <div className={styles.page}>
      <Navbar commonNavigation className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<BrandIcon size={24} style={{ marginRight: 6 }} />}>
          <LanguageToggle />
      </Navbar>

      <main className={styles.mainContainer}>
        <div className={styles.grid}>
          
          {/* LEFT PANEL — Value Props & Guarantees */}
          <div className={styles.trustPanel}>
            <div>
              <h2 className={styles.trustTitle}>{t('completeSubscription', 'Complete your subscription')}</h2>
              <p style={{ color: '#9ca3af', marginTop: 8, fontSize: '0.95rem' }}>
                {t('trustPanelSub', 'Join hundreds of local businesses that trust ReplyShoot to keep their reviews active and build customer trust.')}
              </p>
            </div>

            <div className={styles.bulletList}>
              <div className={styles.bulletItem}>
                <div className={styles.bulletIcon}>✓</div>
                <div className={styles.bulletText}>
                  <strong>{t('guaranteeTitle', 'Cancel Anytime, No Contracts')}</strong><br />
                  {t('guaranteeText', "You're never locked in - cancel your subscription at any time directly from your dashboard.")}
                </div>
              </div>

              <div className={styles.bulletItem}>
                <div className={styles.bulletIcon}>✓</div>
                <div className={styles.bulletText}>
                  <strong>{t('copywritersTitle', 'Personalized, Not Generic')}</strong><br />
                  {t('copywritersText', "Every reply is AI-drafted and matched to your brand's unique voice — never a generic template.")}
                </div>
              </div>

              <div className={styles.bulletItem}>
                <div className={styles.bulletIcon}>✓</div>
                <div className={styles.bulletText}>
                  <strong>{t('controlTitle', 'You Keep Full Control')}</strong><br />
                  {t('controlText', 'No reply is ever published without your approval. Verify every draft with one click via email.')}
                </div>
              </div>
            </div>

            <div className={styles.securityFooter}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>
                {t('secureCheckoutPaddle', 'Secure checkout powered by Paddle. Your payment details are fully encrypted and PCI-DSS compliant.')}
              </span>
            </div>
          </div>

          {/* RIGHT PANEL — Interactive Pricing & Checkout */}
          <div className={styles.card}>
            <h3 className={styles.title} style={{ fontSize: '1.4rem' }}>{t('orderSummary', 'Order Summary')}</h3>
            
            {/* Cycle Pill Switcher */}
            <div className={styles.cycleSwitcher}>
              <button
                type="button"
                className={`${styles.cycleBtn} ${billingCycle === 'monthly' ? styles.cycleBtnActive : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                {t('monthlyLabel', 'Monthly')}
              </button>
              <button
                type="button"
                className={`${styles.cycleBtn} ${billingCycle === 'annual' ? styles.cycleBtnActive : ''}`}
                onClick={() => setBillingCycle('annual')}
              >
                {t('billedAnnually', 'Billed Annually')}
                <span className={styles.saveBadge}>{t('save8', 'Save ~8%')}</span>
              </button>
            </div>

            {/* Selected Plan Details */}
            <div className={styles.planSummaryCard}>
              <div className={styles.planHeader}>
                <span className={styles.planBadge}>{selectedPlan}</span>
                <div className={styles.planPrice}>
                  ${activePrice}
                  <span className={styles.planPriceSub}>{t('perMonth', '/mo')}</span>
                </div>
              </div>
              <p className={styles.planDetails}>{planDetails.details}</p>
              {billingCycle === 'annual' && (
                <p className={styles.planDetails}>{t('billedAsOneCharge', `Billed as one annual charge of $${planDetails.annualPrice}`)}</p>
              )}
            </div>

            {/* Receipt Email Address */}
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('receiptEmail', 'Receipt Email Address')}</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <input
                  type="email"
                  className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                  placeholder={t('receiptEmailPlaceholder', 'you@yourbusiness.com')}
                  required
                />
              </div>
              {emailError && (
                <span className={styles.inputErrorMessage}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {emailError}
                </span>
              )}
            </div>

            {/* CTA Actions */}
            <div className={styles.btnRow}>
              <button type="button" className={styles.checkoutBtn} onClick={startCheckout} disabled={!paddle && !configError}>
                {t('continueToCheckout', 'Continue to Secure Checkout')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                </svg>
              </button>
              <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                {t('goBack', 'Go Back')}
              </button>
            </div>

          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentPage;
